import {
  faPlus as AddIcon,
  faTrashCan as DeleteIcon
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Popper, { type PopperProps } from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import LargeCard from '@/components/LargeCard';
import {
  ticketAtom,
  ticketIsEditingAtom,
  useFetchRecommendationTemplates
} from '@/pages/Ticket';
import { getSessionStorageData } from '@/utils/Common.utils';

import useStyles from './TicketCreateRecommendations.styles';
import type { FormFields } from './TicketCreateRecommendations.types';

const RecommendationTemplatesMenu = (props: PopperProps) => {
  return <Popper {...props} placement="bottom-end" />;
};

const TicketCreateRecommendations = () => {
  const [{ ticket_recommendations }] = useAtom(ticketAtom);
  const [ticketIsEditing] = useAtom(ticketIsEditingAtom);
  const { data: recommendationTemplates = [], isFetching } =
    useFetchRecommendationTemplates();
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext<FormFields>();
  const { append, fields, remove } = useFieldArray<FormFields>({
    name: 'ticket_recommendations'
  });
  const [activeFocus, setActiveFocus] = useState<string | null>(null);
  const { classes } = useStyles();

  const handleAdd = () => {
    append({ body: '', completed: false });

    // close the recommendation template autocomplete component if open
    setActiveFocus(null);
  };

  const handleAutocompleteChange = (index: number, value: string) => {
    setValue(`ticket_recommendations.${index}.body`, value, {
      shouldDirty: true
    });

    // close the recommendation template autocomplete component if open
    setActiveFocus(null);
  };

  const handleErrorMessage = (index: number) => {
    if (errors.ticket_recommendations) {
      if (errors.ticket_recommendations[index]?.body?.type === 'minLength') {
        return 'Recommendation must be a minimum of 3 characters';
      }

      if (errors.ticket_recommendations[index]?.body?.type === 'required') {
        return 'Recommendation required';
      }
    }

    return null;
  };

  // if user reloads the page while editing
  useEffect(() => {
    if (ticketIsEditing) {
      setValue(
        'ticket_recommendations',
        ticket_recommendations as { body: string; completed: boolean }[]
      );
    } else {
      const savedDraft = getSessionStorageData('draft');
      if (!savedDraft) {
        setValue('ticket_recommendations', []);
      }
    }
  }, [setValue, ticket_recommendations, ticketIsEditing]);

  return (
    <LargeCard title="Recommendations">
      <Stack direction="column" spacing={2}>
        <div className={classes.recommendationsFormElement}>
          <Stack>
            {fields?.map((item, index) => {
              return (
                <Stack sx={{ gap: 1 }} key={item.id}>
                  {errors.ticket_recommendations?.[index] && (
                    <Alert severity="error">{handleErrorMessage(index)}</Alert>
                  )}
                  <FormControl
                    className={classes.recommendationsFormControl}
                    id={item.id.toString()}
                  >
                    <Stack direction="row" sx={{ pb: 2 }}>
                      <Stack
                        direction="row"
                        sx={{
                          width: '100%',
                          alignItems: 'center'
                        }}
                      >
                        <Checkbox checked={item.completed} disabled />
                        <FormControl fullWidth>
                          <Controller
                            control={control}
                            name={`ticket_recommendations.${index}.body`}
                            rules={{
                              minLength: 3,
                              required: true
                            }}
                            render={({ field, formState }) => (
                              <TextField
                                id={`recommendation--input-${item.id}`}
                                error={
                                  !!formState.errors?.ticket_recommendations &&
                                  !!formState.errors?.ticket_recommendations[
                                    index
                                  ]
                                }
                                {...field}
                                slotProps={{
                                  htmlInput: {
                                    onFocus: () =>
                                      setActiveFocus(
                                        `recommendation--input-${item.id}`
                                      )
                                  }
                                }}
                                // onFocus removed as it's now handled in slotProps
                              />
                            )}
                          />
                        </FormControl>
                      </Stack>
                      <IconButton
                        className={classes.recommendationsDeleteButton}
                        onClick={() => remove(index)}
                      >
                        <FontAwesomeIcon icon={DeleteIcon} size="sm" />
                      </IconButton>
                    </Stack>
                  </FormControl>
                  {activeFocus === `recommendation--input-${item.id}` && (
                    <div className={classes.recommendationsFormElement}>
                      <InputLabel
                        htmlFor="recommendations-template-autocomplete"
                        sx={{ ml: '2.6rem' }}
                      >
                        Recommendation Templates
                      </InputLabel>
                      <FormControl
                        className={classes.recommendationsFormControl}
                        id={item.id.toString()}
                      >
                        <Autocomplete
                          id={item.id.toString()}
                          disablePortal
                          options={
                            (!isFetching &&
                              recommendationTemplates.map(
                                (recommendation) => recommendation.template
                              )) ||
                            []
                          }
                          slotProps={{
                            popper: {
                              component: RecommendationTemplatesMenu
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              className={classes.recommendationsTemplateInput}
                              onBlur={() => setActiveFocus(null)}
                            />
                          )}
                          onChange={(_, value) => {
                            if (value !== null) {
                              handleAutocompleteChange(index, value);
                            }
                          }}
                        />
                      </FormControl>
                    </div>
                  )}
                </Stack>
              );
            })}
          </Stack>
          <Button
            onClick={handleAdd}
            color="primary"
            startIcon={<FontAwesomeIcon icon={AddIcon} />}
            variant="outlined"
          >
            Add
          </Button>
        </div>
      </Stack>
    </LargeCard>
  );
};

export default TicketCreateRecommendations;
